import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import cx from "classnames";
import styles from "./input.module.scss";
import { Dropdown } from "react-bootstrap";
import _ from "lodash";

interface InputProps {
  autoFocus?: boolean;
  className?: string;
  placeholder?: string;
  value: string;
  // eslint-disable-next-line no-unused-vars
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  disabled?: boolean;
}

export const Input = React.forwardRef<any, InputProps>(({ autoFocus, className, placeholder, value, onChange, onKeyDown, disabled = false}: InputProps, ref) => {
  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inputRef.current!, []);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  return (
    <div className={cx(styles.inputContainer, className)} >
      <input ref={inputRef} className={cx(styles.input, className)} value={value} placeholder={placeholder} onChange={onChange} onKeyDown={onKeyDown} disabled={disabled} />
    </div>
  );
});

Input.displayName = "Input";

interface CheckboxProps {
  className?: string;
  label?: string;
  checked: boolean;
  disabled?: boolean;
  // eslint-disable-next-line no-unused-vars
  onChange: (value: boolean) => void;
  type?: 'checkbox' | 'radio';
}

export const Checkbox: React.FC<CheckboxProps> = ({
  className, checked, disabled, label, onChange, type = 'checkbox'
}) => {

  const handleClick = () => {
    if (!disabled) {
      onChange(!checked);
    }
  }

  return (
    <div className={cx(styles.checkboxContainer, className, {
      [styles.checked]: checked,
      [styles.disabled]: disabled
    })} onClick={handleClick}>
      {type === "checkbox" && <i className={cx(styles.icon, {
        "fa-solid fa-circle-check": checked,
        "fa-regular fa-circle": !checked
      })}></i>}

      {type === "radio" && <i className={cx(styles.icon, {
        "paperflite-radio-button-selected": checked,
        "paperflite-radio-button-unselected": !checked
      })}></i>}
      {label && <span className={styles.label}>{label}</span>}
    </div>
  );
};

interface AutoGrowInputProps {
  className?: string;
  placeholder?: string;
  value: string;
  maxWidth?: number;
  readOnly?: boolean;
  disabled?: boolean;
  // eslint-disable-next-line no-unused-vars
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const AutoGrowInput: React.FC<AutoGrowInputProps> = ({
  className, value, placeholder, disabled, readOnly, maxWidth = 300, onChange,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const hiddenRef = React.useRef<HTMLSpanElement>(null);
  const [width, setWidth] = useState(0);

  const fontStyles = useMemo(() => {
    if (inputRef.current) {
      const properties = window.getComputedStyle(inputRef.current);
      const fontSize = properties.getPropertyValue("font-size");
      const fontFamily = properties.getPropertyValue("font-family");
      return { fontSize, fontFamily };
    }

    return {};
  }, [inputRef.current]);

  useEffect(() => {
    if (hiddenRef.current) {
      let newWidth = hiddenRef.current.offsetWidth + 10;
      if (!value) {
        newWidth = Math.max(newWidth, 40);
      }
      newWidth = Math.min(newWidth, maxWidth);
      setWidth(newWidth);
    }
  }, [value, fontStyles]);

  return (
    <div className={cx(styles.inputContainer, className)}>
      <span className={cx(styles.hidden)} style={fontStyles} ref={hiddenRef}>{value || placeholder}</span>
      <input className={cx(styles.input)}
        ref={inputRef}
        style={{ width }}
        disabled={disabled}
        readOnly={readOnly}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
      />
    </div>
  );
};

interface SearchBarProps {
  query: string | null;
  // eslint-disable-next-line no-unused-vars
  onSearch: (query: string | null) => void;
}

export const SearchBar: React.FC<SearchBarProps> = ({ query, onSearch }) => {
  const [value, setValue] = useState<string>("");

  useEffect(() => {
    setValue(query || "");
  }, [query]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setValue(text);
    if (text === "") {
      onSearch(null);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSearch(value ? value.trim() : null);
  };

  const handleClear = () => {
    setValue("");
    onSearch(null);
  };

  return (
    <div className={cx(styles.searchBar)}>
      <i className={cx(styles.icon, "paperflite-search")}></i>
      <form onSubmit={handleSubmit}>
        <input
          type="text"
          placeholder="Type here to search..."
          value={value}
          onChange={handleChange}
        />
        {value && (
          <i
            className={cx(
              styles.icon,
              styles.clear,
              "fa-solid fa-circle-xmark",
            )}
            onClick={handleClear}
          ></i>
        )}
      </form>
    </div>
  )
};

interface InputWithIconProps {
  className?: string;
  placeholder?: string;
  value?: string;
  icon?: string;
  type?: string;
  disabled?: boolean
  maxLength?: number;
  // eslint-disable-next-line no-unused-vars
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  // eslint-disable-next-line no-unused-vars
  onIconClick?: (e: React.MouseEvent<HTMLElement>) => void;
  // eslint-disable-next-line no-unused-vars
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  // eslint-disable-next-line no-unused-vars
}
export const InputWithIcon: React.FC<InputWithIconProps> = ({
  className, placeholder, value, icon, onChange, onIconClick, type, onClick, disabled = false, maxLength
}) => {
  return (
    <div className={cx(styles.iconInputContainer, className)} onClick={onClick}>
      <input type={type ? type : 'text'} disabled={disabled} maxLength={maxLength} autoComplete="off" className={cx(styles.iconInput)} value={value} placeholder={placeholder} onChange={onChange} />
      <div className={styles.iconContainer}>

        {icon && <i className={cx(styles.icon, icon)} onClick={onIconClick}></i>}
      </div>
    </div>
  );
}

interface SelectInputProps {
  className?: string;
  selected?: string | null;
  placeholder?: string;
  options: {
    icon?: string;
    label: string;
    value: string;
  }[];
  // eslint-disable-next-line no-unused-vars
  onChange?: (value: string) => void;
}

export const SelectInput: React.FC<SelectInputProps> = ({ className, selected, placeholder, options, onChange }) => {
  const selectedOption = useMemo(() => {
    return options.find(option => option.value === selected);
  }, [selected, options]);

  const handleSelect = (value: string) => {
    if (onChange) {
      onChange(value);
    }
  }

  return (
    <Dropdown className={cx(styles.selectDropdown, className)} placement="bottom">
      <Dropdown.Toggle>
        {selectedOption && (
          <>
            {selectedOption?.icon && (
              <img src={selectedOption.icon} alt={selectedOption.label} />
            )}
            {selectedOption?.label}
          </>
        )}

        {!selectedOption && placeholder}
        {_.size(options) > 1 && <i className="paperflite-dropdown"></i>}
      </Dropdown.Toggle>

      {_.size(options) > 1 && (
        <Dropdown.Menu
          popperConfig={{
            strategy: "fixed",
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, 6],
                },
              },
              {
                name: "sameWidth",
                enabled: true,
                phase: "beforeWrite",
                requires: ["computeStyles"],
                fn: ({ state }) => {
                  state.styles.popper.width = `${state.rects.reference.width}px`;
                },
                effect: ({ state }) => {
                  state.elements.popper.style.width = `${
                    state.elements.reference.offsetWidth
                  }px`;
                }
              } as any,
            ],
          }}
        >
          {options.map((option, index) => {
            const selected = option.value === selectedOption?.value;
            return (
              <Dropdown.Item
                key={index}
                className={cx({ [styles.active]: selected })}
                onClick={() => handleSelect(option.value)}
              >
                <span>{option.label}</span>
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      )}
    </Dropdown>
  );

}
